import { z } from 'zod';

const COORDINATES_INVALID_MESSAGE =
  'materialCommodityOrigins.cards.productionPlace.validation.coordinatesInvalid';

const productionPlaceCoordinateSchema = z.tuple([z.number(), z.number()]);

const productionPlaceLinearRingSchema = z
  .array(productionPlaceCoordinateSchema)
  .min(4, COORDINATES_INVALID_MESSAGE)
  .superRefine((ring, context) => {
    const firstCoordinate = ring[0];
    const lastCoordinate = ring.at(-1);

    if (
      !(firstCoordinate && lastCoordinate) ||
      firstCoordinate[0] !== lastCoordinate[0] ||
      firstCoordinate[1] !== lastCoordinate[1]
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: COORDINATES_INVALID_MESSAGE,
      });
    }
  });

const productionPlaceCoordinatesSchema = z.object({
  type: z.literal('Polygon'),
  coordinates: z.array(productionPlaceLinearRingSchema),
});

const productionPlaceMultiPolygonCoordinatesSchema = z.object({
  type: z.literal('MultiPolygon'),
  coordinates: z.array(z.array(productionPlaceLinearRingSchema)),
});

const productionPlaceGeometrySchema = z
  .union([
    productionPlaceCoordinatesSchema,
    productionPlaceMultiPolygonCoordinatesSchema,
  ])
  .superRefine((geometry, context) => {
    const hasPolygon =
      geometry.type === 'Polygon'
        ? geometry.coordinates.length > 0
        : geometry.coordinates.some((polygon) => polygon.length > 0);

    if (!hasPolygon) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: COORDINATES_INVALID_MESSAGE,
      });
    }
  });

export const productionPlaceSchema = z.object({
  countryId: z
    .string()
    .trim()
    .min(
      1,
      'materialCommodityOrigins.cards.productionPlace.validation.countryRequired',
    ),
  description: z
    .string()
    .trim()
    .min(
      1,
      'materialCommodityOrigins.cards.productionPlace.validation.descriptionRequired',
    ),
  forestTypeId: z
    .string()
    .trim()
    .min(
      1,
      'materialCommodityOrigins.cards.productionPlace.validation.forestTypeRequired',
    ),
  productionTimeRange: z
    .string()
    .trim()
    .min(
      1,
      'materialCommodityOrigins.cards.productionPlace.validation.productionTimeRangeRequired',
    ),
  coordinates: productionPlaceGeometrySchema,
});

export type ProductionPlaceFormValues = z.infer<typeof productionPlaceSchema>;
